import { ChangeDetectorRef, Component, DOCUMENT, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivationEnd, ActivationStart, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, combineLatest, of, withLatestFrom } from 'rxjs';
import { buffer, catchError, defaultIfEmpty, filter, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';
import { DigitaalAdresResource, LijstVanTelefoonnummers } from '@ap/interface-apic-digitaal-adres';
import { SessiebeheerResource, SessionStatus, UserType } from '@ap/interface-apic-sessiebeheer';
import { KeepAliveModalClosedReason } from '@ap/keep-alive';
import { MijnAuthSessieManagerService } from '@ap/mijn-auth-sessie-manager';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnOmgevingTemplates, TemplateSettings } from '@ap/mijn-template';
import { WebpartMessagingService } from '@ap/mijn-webpart-messaging';
import { ApPersistenceService } from '@ap/persistence';
import { ApWebContainerService, NativeProcessStatus } from '@ap/web-container';

import { BusinessCustomerService } from './guards/business-customer/business-customer.service';
import { CONVERSATIONAL_CHAT_SESSION, NBA_SESSION, OVERBOEKEN_SESSION } from './mijn-omgeving.constant';
import content from './mijn-omgeving.content.json';
import { MijnOmgevingNavigation } from './mijn-omgeving.navigation';
import { defaultEvents } from './mijn-omgeving.router.module';
import { MijnWebcomponentService } from './mijn-webcomponent.service';
/**
 * @module MijnOmgevingModule
 * @ngdoc component
 * @name MijnOmgevingComponent
 * @description
 */
@Component({
    selector: 'mijn-omgeving',
    templateUrl: 'mijn-omgeving.component.html',
    styleUrls: ['mijn-omgeving.component.scss'],
    standalone: false
})
export class MijnOmgevingComponent implements OnInit, OnDestroy {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly businessCustomerService = inject(BusinessCustomerService);
    private readonly contentService = inject(ApContentService);
    private readonly digitaalAdresResource = inject(DigitaalAdresResource);
    private readonly launchDarklyService = inject(MijnLaunchDarklyService);
    private readonly mijnOmgevingNavigation = inject(MijnOmgevingNavigation);
    private readonly persistenceService = inject(ApPersistenceService);
    private readonly sessiebeheerResource = inject(SessiebeheerResource);
    private readonly router = inject(Router);
    private readonly windowService = inject(ApWindowService);
    private readonly webcomponentService = inject(MijnWebcomponentService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly webpartMessagingService = inject(WebpartMessagingService);
    private readonly auth = inject(MijnAuthSessieManagerService);
    private readonly apWebContainerService = inject(ApWebContainerService);

    public readonly timeToThreshold: number = 8 * 60 * 1000; // 8 minutes
    public readonly timeToEnd: number = 10 * 60 * 1000; // 10 minutes
    public showKeepAlive: boolean;
    public isMobileRoute = false;
    private requiresAuth = true;
    public currentTemplate: BehaviorSubject<MijnOmgevingTemplates> = new BehaviorSubject(MijnOmgevingTemplates.EMPTY);
    public templateSettings: BehaviorSubject<TemplateSettings> = new BehaviorSubject({
        title: '',
        breadcrumb: { title: '' },
        loading: true,
        webcomponentIdentifier: null
    });
    private readonly subscription: Subscription = new Subscription();
    private readonly minimumClassificationCode = 10;
    private readonly digicodeAuthenticationLevel = 30;
    private readonly destroyPhonenumberValidation = new Subject<void>();
    private isPhoneValidationTakeoverEnabled = new BehaviorSubject<boolean>(false);
    private isProspect: boolean;

    @HostListener('window:dispatch-webpart-message', ['$event'])
    dispatchMessage(event: Event) {
        this.webpartMessagingService.onTransportEvent(event as CustomEvent);
    }

    public ngOnInit(): void {
        this.auth.appState$.subscribe((appState) => {
            const returnTo = appState?.returnTo;
            if (returnTo != null && this.validReturnToLocation(returnTo)) {
                window.location.assign(returnTo);
            }
        });
        // with usehash false the urls with a hash wil all show the overview page unless we redirect them using the snippet below
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                let eventUrl = event.url;
                if (!!eventUrl && eventUrl.match(/\/([0-9a-zA-Z-_]+)\/(?:\1)(\/|$)/)) {
                    if (this.mijnOmgevingNavigation.getCurrentUrl()) {
                        this.router.navigateByUrl(this.mijnOmgevingNavigation.getCurrentUrl());
                    } else {
                        this.navigateToCorrectedUrl(eventUrl);
                    }
                }
                if (!!eventUrl && eventUrl.match(/^\/#/)) {
                    eventUrl = eventUrl.replace('/#/', '');
                    if (eventUrl.match(/^.+\/$/)) {
                        eventUrl = eventUrl.substring(0, eventUrl.length - 1);
                    }
                    this.windowService.getWindow().location.replace(document.baseURI + eventUrl);
                    return;
                }
            }
        });
        this.contentService.addContentItems(content);

        const navigationEnd$ = this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            tap((event) => {
                this.mijnOmgevingNavigation.setCurrentUrl((event as NavigationEnd).url);
                let route = this.router.routerState.root;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                this.isMobileRoute = !!route.snapshot?.data?.isMobile;
                this.requiresAuth = route.snapshot?.data?.requiresAuth !== false;
            })
        );

        this.subscription.add(
            navigationEnd$
                .pipe(
                    mergeMap(() => this.sessiebeheerResource.getSession('0').pipe(catchError(() => of(null)))),
                    withLatestFrom(this.auth.isAuthenticated$),
                    tap(([, authenticated]) => {
                        if (authenticated) {
                            this.auth
                                .refreshAccessToken()
                                .pipe(catchError(() => of(null)))
                                .subscribe();
                        }
                    })
                )
                .subscribe(([session, authenticated]) => {
                    this.showKeepAlive = !this.isMobileRoute && ((session && session.status === SessionStatus.ACTIVE) || authenticated);
                    this.isProspect = session?.user?.type.includes(UserType.PROSPECT);
                    if (
                        this.requiresAuth &&
                        !authenticated &&
                        (!session?.user || (session?.user && session.user.type.includes(UserType.ANONYMOUS)))
                    ) {
                        if (this.isMobileRoute) {
                            this.apWebContainerService.finishProcess(NativeProcessStatus.FAILED);
                        } else {
                            const orgUrl = encodeURIComponent(this.mijnOmgevingNavigation.getCurrentUrl());
                            this.mijnOmgevingNavigation.external(
                                this.contentService.get('mijn.omgeving.login.redirect.noMessage.url', { orgUrl })
                            );
                        }
                    }
                })
        );

        this.subscription.add(this.businessCustomerService.isBusinessCustomer().subscribe());

        this.document.querySelector('mijn-omgeving').addEventListener('templateSettings', (settings: CustomEvent) => {
            if (!!settings.detail?.setTemplate) {
                this.currentTemplate.next(settings.detail.setTemplate);
            }
            this.templateSettings.next({ ...settings.detail });
        });

        this.subscription.add(
            this.router.events
                // We filter the event using deployurl to only catch the webcomponent routerevent and not the toplevel routerevent.
                // Without this filter there is a second routerevent containing nothing except Symbol(Routename) which is created by Angular internally.
                .pipe(filter((event) => event instanceof ActivationStart && !!event.snapshot?.data?.deployUrl))
                .subscribe((event: ActivationStart) => {
                    this.currentTemplate.next(event.snapshot.data.dynamicTemplate ?? MijnOmgevingTemplates.EMPTY);
                    if (event.snapshot.data.dynamicTemplate) {
                        this.templateSettings.next({
                            ...this.templateSettings.getValue(),
                            changeConversationalStatus: null,
                            loading: true
                        });
                    }
                })
        );

        this.setupCheckForPhoneValidationNeeded();

        this.registerMijnOmgevingForNavigationEvents();

        // This fixes NG0100 error
        this.changeDetector.detectChanges();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public keepAlive(): void {
        this.auth.isAuthenticated$
            .pipe(
                take(1),
                switchMap((authenticated) => (authenticated ? this.auth.refreshAccessToken().pipe(catchError(() => of(null))) : of(null)))
            )
            .subscribe();
        this.sessiebeheerResource.getSession('0').subscribe();
    }

    public keepAliveModalClosed(reason: KeepAliveModalClosedReason): void {
        if (reason === KeepAliveModalClosedReason.ACCEPTED) {
            this.keepAlive();
        } else {
            const currentUrl = this.mijnOmgevingNavigation.getCurrentUrl();
            let redirectUrl = this.contentService.get('mijn.omgeving.login.redirect.url', { orgUrl: encodeURIComponent(currentUrl) });
            if (this.isProspect && this.persistenceService.getSession('mijn-login', 'context') === 'huismap') {
                redirectUrl = this.contentService.get('mijn.omgeving.login.redirect.huismap.url');
            }
            this.auth.isAuthenticated$.subscribe((result) => {
                if (result) {
                    this.auth.logout().subscribe(() => {
                        this.cleanupAndRedirect(redirectUrl);
                    });
                } else {
                    this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED }).subscribe(() => {
                        this.cleanupAndRedirect(redirectUrl);
                    });
                }
            });
        }
    }

    private cleanupAndRedirect(redirectUrl: string) {
        this.persistenceService.clearSession(CONVERSATIONAL_CHAT_SESSION);
        this.persistenceService.clearSession(NBA_SESSION);
        this.persistenceService.clearSession(OVERBOEKEN_SESSION);
        this.windowService.getWindow().history.replaceState({}, 'login redirect', redirectUrl);
        this.windowService.getWindow().history.go(0);
    }

    private registerMijnOmgevingForNavigationEvents(): void {
        defaultEvents.forEach((event) => this.webcomponentService.addWebcomponentEventListener('mijn-omgeving', event));
    }

    private setupCheckForPhoneValidationNeeded(): void {
        this.launchDarklyService.getFlagValue('migrate-force-phone-number-validation', false).subscribe((bool) => {
            this.isPhoneValidationTakeoverEnabled.next(bool);
            if (!bool) {
                this.destroyPhonenumberValidation.complete();
            }
        });

        this.subscription.add(
            combineLatest([this.router.events, this.isPhoneValidationTakeoverEnabled])
                .pipe(
                    buffer(
                        this.router.events.pipe(
                            filter((event) => event instanceof ActivationStart || event instanceof ActivationEnd),
                            filter(() => this.isPhoneValidationTakeoverEnabled.getValue() === true)
                        )
                    ),
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    switchMap((_) => this.sessiebeheerResource.getSession('0')),
                    switchMap((session) => {
                        if (session.authentication_level === this.digicodeAuthenticationLevel) {
                            return this.digitaalAdresResource.getTelefoonnummers(null, true);
                        }
                        return EMPTY;
                    }),
                    switchMap<LijstVanTelefoonnummers, Observable<boolean | string>>((response) => {
                        if (response.telefoonnummers && response.telefoonnummers.length > 0) {
                            if (
                                response.telefoonnummers.every(
                                    (telefoonnummer) => telefoonnummer.classificatie_code <= this.minimumClassificationCode
                                )
                            ) {
                                return of(response.telefoonnummers[0].nummer);
                            }
                            return of(false);
                        }
                        // No phonenumbers known for this customer
                        return of(true);
                    }),
                    defaultIfEmpty(false),
                    takeUntil(this.destroyPhonenumberValidation),
                    catchError(() => of(false))
                )
                .subscribe((phonenumber) => {
                    if (phonenumber && !this.router.url.includes('contactgegevens-beheren')) {
                        if (/^[0-9]+$/.test(String(phonenumber))) {
                            this.persistenceService.setLocal('telefoonnummer-validatie', 'telefoonnummer', phonenumber as string);
                        }

                        this.mijnOmgevingNavigation.toTelefoonnummerValidatie();
                    } else if (!phonenumber) {
                        this.destroyPhonenumberValidation.complete();
                    }
                })
        );
    }

    private navigateToCorrectedUrl(eventUrl: string): void {
        let correctedUrl = eventUrl;
        if (eventUrl.indexOf('#') === eventUrl.length - 1) {
            correctedUrl = eventUrl.slice(0, -1);
        }
        const pathnameArray = correctedUrl.split('/');
        correctedUrl = [...new Set(pathnameArray)].join('/');
        this.router.navigateByUrl(correctedUrl);
    }

    private validReturnToLocation(location: string) {
        const domainPattern = this.contentService.get('mijn.omgeving.returnTo.domainPattern');
        try {
            const url = new URL(location);
            const domainRegex = new RegExp(`^${domainPattern}$`);
            return domainRegex.test(url.hostname) && url.pathname.startsWith(this.contentService.get('mijn.omgeving.returnTo.basepath'));
        } catch {
            return false;
        }
    }
}
